import { useEffect, useRef, useState } from "react";
import { ensureGisInitialized, registerLoginCredentialHandler } from "../utils/gisSilentRefresh.js";

// A visible Google button is the interactive fallback when One Tap is suppressed.
export default function GoogleCredentialButton({ onCredential, busy = false }) {
  const host = useRef(null);
  const receive = useRef(onCredential);
  const busyRef = useRef(busy);
  receive.current = onCredential;
  busyRef.current = busy;
  const [phase, setPhase] = useState("loading");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer;
    const started = Date.now();
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    const unregister = registerLoginCredentialHandler(credential => {
      if (!cancelled && !busyRef.current) receive.current(credential);
    }, { interactive: true });
    setPhase("loading");
    const prepare = () => {
      if (cancelled) return;
      if (!clientId) { setPhase("config"); return; }
      if (!window.google?.accounts?.id) {
        if (Date.now() - started >= 8000) { setPhase("error"); return; }
        timer = setTimeout(prepare, 200);
        return;
      }
      try {
        const gsi = ensureGisInitialized({ google: window.google, clientId });
        host.current.replaceChildren();
        gsi.renderButton(host.current, {
          type: "standard", theme: "outline", size: "large", text: "continue_with",
          shape: "rectangular", width: Math.min(320, Math.floor(host.current.clientWidth)),
        });
        setPhase("ready");
      } catch { setPhase("error"); }
    };
    prepare();
    return () => { cancelled = true; clearTimeout(timer); unregister(); };
  }, [retry]);
  return <div className="mpx-google-recovery" aria-busy={busy}>
    <div ref={host} className="mpx-google-recovery__button" inert={busy ? "" : undefined} data-testid="reauth-google-button" />
    {phase === "loading" && <p role="status">Google 로그인 버튼을 준비하고 있습니다.</p>}
    {phase === "config" && <p role="alert">Google 로그인 설정을 확인할 수 없습니다. 관리자에게 문의해 주세요.</p>}
    {phase === "error" && <div role="alert"><p>Google 로그인 버튼을 불러오지 못했습니다. 연결을 확인해 주세요.</p>
      <button type="button" className="mpx-btn mpx-btn--secondary" onClick={() => setRetry(value => value + 1)}>로그인 버튼 다시 불러오기</button>
    </div>}
  </div>;
}
